import React, { useState, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import Logo from 'components/common/Logo';
import Link from 'components/common/link';

// Common Header Components
import MainMenu from './partials/main-menu';
// import SearchForm from './partials/search-form';
import UserMenu from './partials/user-menu';
import CartMenu from './partials/cart-menu';
import { useDeliverySlotsContext } from 'lib/deliveryDateTimes';
import { CountDownTimer } from 'lib/datetime/components';
import { CutOffReminder } from './v2';
import { useRouter } from 'next/router';
import { useAuth } from 'lib/auth';
import useMobile from 'hooks/useMobile';
import useScroll from 'hooks/useScroll';
import cn from 'classnames';
import config, { routePaths } from 'appConfig';
import { COUPON_CODE_QUERY_PARAM } from 'lib/coupons';

function Header(props) {
  const {
    container = 'container',
    cateringEnabled,
    ybExpressEnabled,
  } = props;

  const {
    nextCutOffTime,
    refreshCutOffTime,
  } = useDeliverySlotsContext();
  const [showCutOffTimeBanner, setShowCutOffTimeBanner] = useState(
    true,
  );
  const [
    showWelcomeNotification,
    setShowWelcomeNotification,
  ] = useState(true);

  const _refreshCutOffTimeSlot = useCallback(
    (cb) => {
      refreshCutOffTime();
      cb && cb();
    },
    [refreshCutOffTime],
  );

  const router = useRouter();
  const pathname = router.pathname;
  const { session, loading } = useAuth();
  const isMobile = useMobile(992);
  const isScroll = useScroll(10);

  const shouldTransparent =
    pathname === '/' && !isMobile && !session?.user;
  const isCateringPage = pathname === routePaths.CATERING;

  if (loading) return null;

  const withWelcomeNotification = !session?.user;

  const showBanner =
    showCutOffTimeBanner &&
    (!withWelcomeNotification || !isCateringPage);

  if (shouldTransparent) {
    return (
      <div
        className={cn(
          'tw-fixed tw-left-0 tw-right-0 tw-z-[1040]',
          isScroll && 'tw-shadow-[0_3px_6px_rgba(51,51,51,0.05)]',
        )}
      >
        {showWelcomeNotification && (
          <div className="notification">
            <div className="notify-content tw-mx-0 tw-px-4">
              <h3>
                <strong>$10 OFF + FREE Delivery ($50+)</strong> with
                code <strong>{config.welcomeCouponCode}</strong> — for
                first-timers only!{' '}
                <Link
                  className="tw-text-white tw-underline tw-font-medium"
                  to={`${routePaths.CATEGORIES}?${COUPON_CODE_QUERY_PARAM}=${config.welcomeCouponCode}`}
                >
                  Order Now.
                </Link>
              </h3>
              <div className="notify-action">
                <Link to="#">
                  <i
                    className="icon-close"
                    onClick={() => setShowWelcomeNotification(false)}
                  ></i>
                </Link>
              </div>
            </div>
          </div>
        )}
        <header
          className={cn(
            'header tw-transition-colors tw-duration-200',
            isScroll ? 'tw-bg-white' : 'tw-bg-transparent',
          )}
        >
          <div
            className={cn(
              'header-bottom tw-transition-colors tw-duration-200',
              isScroll
                ? 'tw-bg-white'
                : 'tw-bg-transparent header-bottom-transparent',
            )}
          >
            <div className={container} style={{ height: '80px' }}>
              <div className="header-left">
                <div className="tw-relative tw-flex">
                  <Link to={'/'} className="logo">
                    <Logo
                      variant="logo-horizontal"
                      withRegisteredBadge
                    />
                  </Link>
                </div>
              </div>

              <div className="header-center link-shadow tw-mr-10">
                <MainMenu
                  showPerks={false}
                  cateringEnabled={cateringEnabled}
                  ybExpressEnabled={ybExpressEnabled}
                />
              </div>

              <div className="header-right">
                <Container fluid style={{ padding: 0 }}>
                  <div className="tw-flex tw-flex-nowrap tw-items-center tw-gap-4 lg:tw-gap-6 xl:tw-gap-8">
                    <div className="tw-flex">
                      <UserMenu
                        asButton={withWelcomeNotification}
                        shadow={!isScroll}
                      />
                    </div>
                    <div className="tw-flex">
                      <CartMenu withTotal={!isMobile} />
                    </div>
                  </div>
                </Container>
              </div>
            </div>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="sticky-header">
      {showBanner && (
        <div className="notification">
          <div className="notify-content tw-mx-0 tw-px-4">
            {withWelcomeNotification ? (
              <h3>
                <strong>$10 OFF + FREE Delivery ($50+)</strong> with
                code <strong>{config.welcomeCouponCode}</strong> — for
                first-timers only!{' '}
                <Link
                  className="tw-text-white tw-underline tw-font-medium"
                  to={`${routePaths.CATEGORIES}?${COUPON_CODE_QUERY_PARAM}=${config.welcomeCouponCode}`}
                >
                  Order Now.
                </Link>
              </h3>
            ) : (
              <h3>
                Orders for next delivery will cut off in{' '}
                <span className="timer tw-m-0">
                  {
                    <CountDownTimer
                      value={nextCutOffTime}
                      onComplete={_refreshCutOffTimeSlot}
                    />
                  }
                </span>
                <CutOffReminder />
              </h3>
            )}
          </div>
          <div
            className={cn(
              'notify-action',
              withWelcomeNotification && 'tw-right-4',
            )}
          >
            <Link to="#">
              <i
                className="icon-close"
                onClick={() => setShowCutOffTimeBanner(false)}
              ></i>
            </Link>
          </div>
        </div>
      )}

      <header className="header">
        <div className="header-bottom">
          <div className={container} style={{ height: '80px' }}>
            <div className="header-left">
              <button className="mobile-menu-toggler">
                <span className="sr-only">Toggle mobile menu</span>
                <i className="icon-bars"></i>
              </button>

              <div className="tw-relative tw-flex">
                <Link to={'/'} className="logo">
                  <Logo
                    variant="logo-horizontal"
                    withRegisteredBadge
                  />
                </Link>
              </div>
            </div>

            <div className="header-center tw-mr-10">
              <MainMenu
                cateringEnabled={cateringEnabled}
                ybExpressEnabled={ybExpressEnabled}
              />
            </div>

            <div className="header-right">
              <Container fluid style={{ padding: 0 }}>
                <div
                  className={cn(
                    'tw-flex tw-flex-nowrap tw-items-center tw-gap-4',
                    session ? 'tw-gap-12' : 'lg:tw-gap-6 xl:tw-gap-8',
                  )}
                >
                  <div className="tw-flex tw-max-w-min">
                    <UserMenu
                      asButton={withWelcomeNotification}
                      shadow={shouldTransparent}
                    />
                  </div>
                  <div className="tw-flex">
                    <CartMenu withTotal={!isMobile} />
                  </div>
                </div>
              </Container>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}

export default Header;
